import { parseEventDate, formatDateTimeForInput, formatLocalDateTimeToMySQL } from '../utils/date-helpers.js';
import { escapeHtml } from '../utils/html-utils.js';

// Event Create/Edit Component
class EventEdit extends HTMLElement {
  constructor() {
    super();
    this.externalLinks = [];
    this.event = null;
    this.loading = false;
    this.isEditMode = false;
    this.eventType = 'one-time'; // 'one-time', 'recurring', or 'multi-day'
    this.recurrencePattern = 'weekly';
    this.occurrences = []; // For multi-day events
    const defaultTimezone = this.getDefaultTimezone();
    this.timezoneOptions = this.getSupportedTimezones(defaultTimezone);
    this.selectedTimezone = this.timezoneOptions.includes(defaultTimezone)
      ? defaultTimezone
      : (this.timezoneOptions[0] || 'UTC');
    /** @type {{ id: number, name: string }[]} */
    this.memberGroups = [];
    /** @type {number|null} */
    this.presetGroupId = null;
  }
  getDefaultTimezone() {
    const guessed = typeof dayjs?.tz === 'function' ? dayjs.tz.guess() : null;
    if (guessed) {
      return guessed;
    }
    if (typeof Intl?.DateTimeFormat === 'function') {
      const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (resolved) {
        return resolved;
      }
    }
    return 'UTC';
  }
  getSupportedTimezones(defaultTimezone) {
    if (typeof Intl.supportedValuesOf === 'function') {
      const zones = Intl.supportedValuesOf('timeZone');
      if (defaultTimezone && !zones.includes(defaultTimezone)) {
        return [defaultTimezone, ...zones];
      }
      return zones;
    }

    const fallbackZones = [
      defaultTimezone,
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
      'Europe/Berlin',
      'Asia/Tokyo',
      'Australia/Sydney'
    ].filter(Boolean);

    return [...new Set(fallbackZones)];
  }

  async connectedCallback() {
    await this.loadMemberGroups();
    const presetRaw = this.getAttribute('preset-group-id');
    if (presetRaw) {
      const parsed = parseInt(presetRaw, 10);
      if (!Number.isNaN(parsed)) {
        this.presetGroupId = parsed;
      }
    }
    const eventId = this.getAttribute('event-id');
    if (eventId) {
      this.isEditMode = true;
      await this.loadEvent(eventId);
    }
    this.render();
  }

  async loadMemberGroups() {
    try {
      if (!window.API) return;
      const data = await window.API.request('/groups');
      this.memberGroups = data.groups || [];
    } catch (_e) {
      this.memberGroups = [];
    }
  }

  async loadEvent(eventId) {
    try {
      this.loading = true;
      this.event = await window.API.request(`/events/${eventId}`);

      // Determine event type from loaded data
      if (this.event.is_recurring) {
        this.eventType = 'recurring';
        this.recurrencePattern = this.event.recurrence_pattern ||
          this.event.recurrence_rule?.pattern ||
          this.recurrencePattern;
      } else if (this.event.is_multi_day) {
        this.eventType = 'multi-day';
      } else {
        this.eventType = 'one-time';
      }

      if (this.event?.timezone) {
        if (!this.timezoneOptions.includes(this.event.timezone)) {
          this.timezoneOptions = [this.event.timezone, ...this.timezoneOptions];
        }
        this.selectedTimezone = this.event.timezone;
      }

      this.loading = false;
      this.externalLinks = (this.event.external_links || []).map((l) => ({ label: l.label || '', url: l.url || '' }));
      this.render();
    } catch (error) {
      window.logger.error('Failed to load event:', error);
      this.loading = false;
      this.innerHTML = `
        <div class="event-edit-container">
          <div class="error">Failed to load event. Please try again.</div>
        </div>
      `;
    }
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
        <div class="event-edit-container">
          <div class="loading">Loading event...</div>
        </div>
      `;
      return;
    }
    // Determine if editing instance or recurring parent
    const editingInstance = this.isEditMode && this.event && this.event.parent_event_id;
    const editingRecurringParent = this.isEditMode && this.event && this.event.is_recurring && !this.event.parent_event_id;
    const containerClass = editingInstance ? 'event-edit-container editing-instance' :
      editingRecurringParent ? 'event-edit-container editing-recurring' :
        'event-edit-container';

    const presetGroup = this.presetGroupId
      ? (this.memberGroups || []).find((g) => g.id === this.presetGroupId)
      : null;
    const lockedGroupName = presetGroup?.name || (this.presetGroupId ? `Group #${this.presetGroupId}` : null);
    const groupFieldHtml = this.presetGroupId && !this.isEditMode
      ? `
            <div class="form-group">
              <label>Group</label>
              <input type="hidden" id="group_id" name="group_id" value="${this.presetGroupId}">
              <p class="group-event-preset-note">
                This event is for <strong>${escapeHtml(lockedGroupName)}</strong>.
              </p>
              <small class="help-text">Only members of this group can see this event.</small>
            </div>
      `
      : `
            <div class="form-group">
              <label for="group_id">Group (optional)</label>
              <select id="group_id" name="group_id">
                <option value="">— None —</option>
                ${(this.memberGroups || []).map((g) => `
                  <option value="${g.id}"
                    ${this.isEditMode && String(this.event?.group_id ?? '') === String(g.id) ? 'selected' : ''}>
                    ${escapeHtml(g.name)}
                  </option>`).join('')}
              </select>
              <small class="help-text">Members see linked events on the group home.</small>
            </div>
      `;

    const visibilityHtml = this.presetGroupId && !this.isEditMode
      ? `
            <div class="form-group">
              <label>Event Visibility</label>
              <p class="group-event-preset-note">🔒 Group members only — this event will not appear in public listings.</p>
              <input type="hidden" name="is_public" value="false">
            </div>
      `
      : `
            <div class="form-group">
              <label>Event Visibility</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input type="radio" name="is_public" value="false" ${!this.isEditMode || !this.event?.is_public ? 'checked' : ''}>
                  <div class="radio-card">
                    <strong>🔒 Private</strong>
                    <small>Only invited people can see this event</small>
                  </div>
                </label>
                <label class="radio-label">
                  <input type="radio" name="is_public" value="true" ${this.isEditMode && this.event?.is_public ? 'checked' : ''}>
                  <div class="radio-card">
                    <strong>🌍 Public</strong>
                    <small>Anyone can see and RSVP to this event</small>
                  </div>
                </label>
              </div>
            </div>

            <div class="form-group" id="publicOptions" style="display: none;">
              <label class="checkbox-label">
                <input type="checkbox" id="show_rsvp_count" name="show_rsvp_count" ${!this.isEditMode || this.event?.show_rsvp_count ? 'checked' : ''}>
                <span>Show total RSVP count publicly</span>
              </label>
              <small class="help-text">
                When enabled, anyone can see how many people are attending.
                When disabled, only organizers can see the total count.
              </small>
            </div>
      `;

    this.innerHTML = `
      <div class="${containerClass}">
        <!-- Party Panda Helper -->
        <div class="party-panda-helper">
          <div class="panda-avatar">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="100" cy="120" rx="45" ry="50" fill="#f0f0f0" class="panda-body"/>
              <circle cx="100" cy="80" r="40" fill="#f0f0f0" class="panda-head"/>
              <circle cx="70" cy="60" r="15" fill="#2d2d2d" class="ear-left"/>
              <circle cx="130" cy="60" r="15" fill="#2d2d2d" class="ear-right"/>
              <ellipse cx="85" cy="75" rx="12" ry="15" fill="#2d2d2d" class="eye-patch-left"/>
              <ellipse cx="115" cy="75" rx="12" ry="15" fill="#2d2d2d" class="eye-patch-right"/>
              <circle cx="85" cy="75" r="5" fill="white" class="eye-white-left"/>
              <circle cx="115" cy="75" r="5" fill="white" class="eye-white-right"/>
              <circle cx="87" cy="75" r="3" fill="#2d2d2d" class="pupil-left"/>
              <circle cx="117" cy="75" r="3" fill="#2d2d2d" class="pupil-right"/>
              <ellipse cx="100" cy="90" rx="6" ry="8" fill="#2d2d2d"/>
              <path d="M 90 95 Q 100 102 110 95" stroke="#2d2d2d" stroke-width="2" fill="none" stroke-linecap="round"/>
              <ellipse cx="65" cy="120" rx="12" ry="25" fill="#2d2d2d" class="arm-left"/>
              <ellipse cx="135" cy="120" rx="12" ry="25" fill="#2d2d2d" class="arm-right"/>
              <ellipse cx="85" cy="160" rx="15" ry="12" fill="#2d2d2d"/>
              <ellipse cx="115" cy="160" rx="15" ry="12" fill="#2d2d2d"/>
              <g class="party-hat">
                <polygon points="100,30 85,60 115,60" fill="#ff6b9d" stroke="#ffd166" stroke-width="2"/>
                <circle cx="100" cy="28" r="5" fill="#ffd166" class="hat-pom"/>
                <line x1="85" y1="60" x2="115" y2="60" stroke="#ff6b9d" stroke-width="3"/>
                <circle cx="95" cy="45" r="2" fill="#ffd166"/>
                <circle cx="105" cy="50" r="2" fill="#00d9ff"/>
                <circle cx="100" cy="55" r="2" fill="#b388ff"/>
              </g>
            </svg>
          </div>
          <div class="panda-tip">
            <p id="pandaTipText">${this.isEditMode ? 'Let\'s make this event even better! 🎉' : 'Let\'s create something amazing together! 🎉'}</p>
          </div>
        </div>

        <div class="event-edit-header">
          <button class="btn-back" id="backBtn">← Back</button>
          <h1>
            ${this.isEditMode ?
    (editingInstance ? 'Edit Event <span class="edit-badge edit-badge-instance">This Event Only</span>' :
      editingRecurringParent ? 'Edit Repeating Event <span class="edit-badge edit-badge-recurring">All Events</span>' :
        'Edit Event') :
    'Create Event'}
          </h1>
        </div>

        <div class="alert-container"></div>
        <form id="eventForm" class="event-form">
          <div class="form-section">
            <h2>Event Details</h2>

            <div class="form-group">
              <label for="title">Event Title *</label>
              <input 
                type="text" 
                id="title" 
                name="title" 
                maxlength="200" 
                required 
                placeholder="e.g., Weekly Game Night"
                value="${this.isEditMode ? escapeHtml(this.event?.title || '') : ''}"
              >
            </div>

            <div class="form-group">
              <label for="description">Description</label>
              <markdown-preview>
                <textarea 
                  class="form-control" 
                  name="description" 
                  id="description" 
                  placeholder="Write here... (Markdown supported!)"
                  rows="6"
                  required
                >${this.isEditMode ? escapeHtml(this.event?.description || '') : ''}</textarea>
              </markdown-preview>
            </div>

            <div class="form-group">
              <label for="location">Location</label>
              <input 
                type="text" 
                id="location" 
                name="location"
                placeholder="e.g., Community Center, 123 Main St"
                value="${this.isEditMode ? escapeHtml(this.event?.location || '') : ''}"
              >
            </div>

            ${groupFieldHtml}

            ${visibilityHtml}
          </div>

          <div class="form-section">
            <h2>Date & Time</h2>

            <!-- Event Type Selector -->
            <div class="form-group">
              <label>Event Type${this.isEditMode ? ' (cannot be changed after creation)' : ''}</label>
              <div class="radio-group">
                <label class="radio-label">
                  <input type="radio" name="event_type" value="one-time" ${this.eventType === 'one-time' ? 'checked' : ''} ${this.isEditMode ? 'disabled' : ''}>
                  <div class="radio-card">
                    <strong>📅 One-Time</strong>
                    <small>Single date and time</small>
                  </div>
                </label>
                <label class="radio-label">
                  <input type="radio" name="event_type" value="recurring" ${this.eventType === 'recurring' ? 'checked' : ''} ${this.isEditMode ? 'disabled' : ''}>
                  <div class="radio-card">
                    <strong>🔄 Recurring</strong>
                    <small>Repeats on a pattern</small>
                  </div>
                </label>
                <label class="radio-label">
                  <input type="radio" name="event_type" value="multi-day" ${this.eventType === 'multi-day' ? 'checked' : ''} ${this.isEditMode ? 'disabled' : ''}>
                  <div class="radio-card">
                    <strong>🎭 Multi-Day</strong>
                    <small>Multiple specific dates</small>
                  </div>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label for="timezone">Timezone</label>
              <select id="timezone" name="timezone">
                ${this.timezoneOptions.map(
    (timezone) => `<option value="${escapeHtml(timezone)}" ${timezone === this.selectedTimezone ? 'selected' : ''}>
                    ${escapeHtml(timezone.replace(/_/g, ' '))}
                  </option>`
  ).join('')}
              </select>
              <small class="help-text">
                Dates and times are stored using this timezone.
              </small>
            </div>

            <!-- One-Time Event Fields -->
            <div id="oneTimeFields" style="display: ${this.eventType === 'one-time' ? 'block' : 'none'};">
              <div class="form-row">
                <div class="form-group">
                  <label for="start_time">Start Date & Time *</label>
                  <input 
                    type="datetime-local" 
                    id="start_time" 
                    name="start_time" 
                    required
                    value="${this.isEditMode && this.event?.start_time ? formatDateTimeForInput(parseEventDate(this.event.start_time, this.selectedTimezone), this.selectedTimezone) : ''}"
                  >
                </div>

                <div class="form-group">
                  <label for="end_time">End Date & Time *</label>
                  <input 
                    type="datetime-local" 
                    id="end_time" 
                    name="end_time"
                    required
                  value="${this.isEditMode && this.event?.end_time ? formatDateTimeForInput(parseEventDate(this.event.end_time, this.selectedTimezone), this.selectedTimezone) : ''}"
                  >
                </div>
              </div>

              <div class="form-group">
                <small class="help-text">
                  📍 Times are saved using the selected timezone.
                </small>
              </div>
            </div>

            <!-- Recurring Event Fields -->
            <div id="recurringFields" style="display: ${this.eventType === 'recurring' ? 'block' : 'none'};">
              ${this.renderRecurringFields()}
            </div>

            <!-- Multi-Day Event Fields -->
            <div id="multiDayFields" style="display: ${this.eventType === 'multi-day' ? 'block' : 'none'};">
              ${this.renderMultiDayFields()}
            </div>
          </div>

          ${editingRecurringParent ? this.renderRsvpReminderSection() : ''}

          <div class="form-section">
            <h2>External Links <small>(optional)</small></h2>
            <p class="help-text">Add links to tickets, venue info, or other relevant websites</p>
            
            <div id="external-links-container">
              ${this.renderExternalLinks()}
            </div>

            <button type="button" class="btn btn-secondary" id="addLinkBtn">
              + Add Link
            </button>
          </div>

          ${this.shouldShowGroupMemberNotify() ? this.renderNotifyGroupMembersSection() : ''}
          ${this.isEditMode && !this.event?.group_id ? this.renderSendUpdateEmailSection() : ''}

          <div class="form-actions">
            <button type="button" class="btn btn-secondary" id="cancelBtn">Cancel</button>
            <button type="submit" class="btn btn-primary" id="submitBtn">${this.isEditMode ? 'Update Event' : 'Create Event'}</button>
          </div>
        </form>
      </div>
    `;

    this.attachEventListeners();
    // Set up the correct event type visibility and required attributes
    this.switchEventType();
    if (!this.isEditMode) {
      this.setDefaultDateTime();
    }
    this.setupPandaTips();
  }

  renderExternalLinks() {
    const links = this.externalLinks;

    if (links.length === 0) {
      return '<p class="no-links-msg">No links added yet</p>';
    }

    return links.map((link, index) => `
      <div class="external-link-input" data-index="${index}">
        <input 
          type="text" 
          placeholder="Label (e.g., Buy Tickets)" 
          value="${escapeHtml(link.label || '')}"
          data-field="label"
          data-index="${index}"
        >
        <input 
          type="url" 
          placeholder="https://..." 
          value="${escapeHtml(link.url || '')}"
          data-field="url"
          data-index="${index}"
        >
        <button type="button" class="btn-remove" data-index="${index}">×</button>
      </div>
    `).join('');
  }

  setDefaultDateTime() {
    // Set default start time to next hour (only in create mode)
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    const dateTimeStr = now.toISOString().slice(0, 16);
    const startTimeInput = this.querySelector('#start_time');
    if (startTimeInput && !startTimeInput.value) {
      startTimeInput.value = dateTimeStr;
    }
  }

  switchEventType() {
    // Toggle visibility of event type fields
    const oneTimeFields = this.querySelector('#oneTimeFields');
    const recurringFields = this.querySelector('#recurringFields');
    const multiDayFields = this.querySelector('#multiDayFields');

    if (oneTimeFields) oneTimeFields.style.display = this.eventType === 'one-time' ? 'block' : 'none';
    if (recurringFields) recurringFields.style.display = this.eventType === 'recurring' ? 'block' : 'none';
    if (multiDayFields) multiDayFields.style.display = this.eventType === 'multi-day' ? 'block' : 'none';

    // Toggle required attributes to prevent HTML5 validation errors on hidden fields
    const oneTimeStart = this.querySelector('#start_time');
    const oneTimeEnd = this.querySelector('#end_time');
    const recurringStart = this.querySelector('#recurring_start_time');
    const recurringEnd = this.querySelector('#recurring_end_time');

    if (oneTimeStart) oneTimeStart.required = this.eventType === 'one-time';
    if (oneTimeEnd) oneTimeEnd.required = this.eventType === 'one-time';
    if (recurringStart) recurringStart.required = this.eventType === 'recurring';
    if (recurringEnd) recurringEnd.required = this.eventType === 'recurring';

    // Toggle required attributes on multi-day occurrence inputs
    const occurrenceInputs = this.querySelectorAll('input[name="occurrence_start_time"]');
    occurrenceInputs.forEach(input => {
      input.required = this.eventType === 'multi-day';
    });

    const occurrenceEndInputs = this.querySelectorAll('input[name="occurrence_end_time"]');
    occurrenceEndInputs.forEach(input => {
      input.required = this.eventType === 'multi-day';
    });

    if (this.eventType === 'recurring') {
      this.updateRecurrencePatternFields();
    }
  }

  attachEventListeners() {
    // Back button
    this.querySelector('#backBtn').addEventListener('click', () => {
      if (this.isEditMode) {
        window.router.navigate(`/events/${this.event.id}`);
      } else {
        window.router.navigate('/events');
      }
    });

    // Cancel button
    this.querySelector('#cancelBtn').addEventListener('click', () => {
      if (this.isEditMode) {
        window.router.navigate(`/events/${this.event.id}`);
      } else {
        if (confirm('Are you sure you want to cancel? Your changes will be lost.')) {
          window.router.navigate('/events');
        }
      }
    });

    // Public/Private toggle
    this.querySelectorAll('input[name="is_public"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        const publicOptions = this.querySelector('#publicOptions');
        publicOptions.style.display = e.target.value === 'true' ? 'block' : 'none';
      });
    });

    const timezoneSelect = this.querySelector('#timezone');
    if (timezoneSelect) {
      timezoneSelect.addEventListener('change', (e) => {
        this.selectedTimezone = e.target.value;
      });
    }

    // Add link button
    this.querySelector('#addLinkBtn').addEventListener('click', () => {
      this.externalLinks.push({ label: '', url: '' });
      this.updateLinksSection();
    });

    // Event type toggle
    this.querySelectorAll('input[name="event_type"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        this.eventType = e.target.value;
        this.switchEventType();
      });
    });

    // Recurring pattern toggle
    const recurrencePattern = this.querySelector('#recurrence_pattern');
    if (recurrencePattern) {
      recurrencePattern.addEventListener('change', (event) => {
        this.recurrencePattern = event.target.value;
        this.updateRecurrencePatternFields();
      });
    }

    // RSVP Reminder toggle
    const rsvpReminderEnabled = this.querySelector('#rsvp_reminder_enabled');
    if (rsvpReminderEnabled) {
      rsvpReminderEnabled.addEventListener('change', (e) => {
        const fields = this.querySelector('#rsvpReminderFields');
        if (fields) {
          fields.style.display = e.target.checked ? 'block' : 'none';
        }
      });
    }

    const sendUpdateEmail = this.querySelector('#send_update_email');
    if (sendUpdateEmail) {
      sendUpdateEmail.addEventListener('change', (e) => {
        const fields = this.querySelector('#sendUpdateEmailFields');
        if (fields) {
          fields.style.display = e.target.checked ? 'block' : 'none';
        }
      });
    }

    const notifyGroupMembers = this.querySelector('#notify_group_members');
    if (notifyGroupMembers) {
      notifyGroupMembers.addEventListener('change', (e) => {
        const fields = this.querySelector('#notifyGroupMembersFields');
        if (fields) {
          fields.style.display = e.target.checked ? 'block' : 'none';
        }
      });
    }

    // Monthly pattern type toggle
    this.querySelectorAll('input[name="monthly_pattern_type"]').forEach(radio => {
      radio.addEventListener('change', (e) => {
        const patternType = e.target.value;
        const exactFields = document.getElementById('monthlyExactFields');
        const relativeFields = document.getElementById('monthlyRelativeFields');
        if (exactFields) exactFields.style.display = patternType === 'exact' ? 'block' : 'none';
        if (relativeFields) relativeFields.style.display = patternType === 'relative' ? 'block' : 'none';
      });
    });

    // Multi-day: Add occurrence
    const addOccurrenceBtn = this.querySelector('#addOccurrenceBtn');
    if (addOccurrenceBtn) {
      addOccurrenceBtn.addEventListener('click', () => {
        // First sync any existing values
        this.syncOccurrencesFromDOM();
        // Then add a new empty occurrence
        this.occurrences.push({ start_time: '', end_time: '' });
        // Finally re-render
        this.updateOccurrencesSection();
      });
    }

    // Multi-day: Remove occurrence (will be reattached by updateOccurrencesSection)

    // Form submission
    this.querySelector('#eventForm').addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Markdown toolbar setup
    const toolbar = this.querySelector('markdown-toolbar');
    const descriptionTextarea = this.querySelector('#description');
    if (toolbar && descriptionTextarea) {
      toolbar.setTextarea(descriptionTextarea);
    }

    this.updateRecurrencePatternFields();
  }

  syncOccurrencesFromDOM() {
    const container = this.querySelector('#occurrences-container');
    if (!container) return;

    const existingInputs = container.querySelectorAll('.occurrence-input');

    // Build new occurrences array from existing inputs
    const syncedOccurrences = [];
    existingInputs.forEach((occurrenceEl) => {
      const startInput = occurrenceEl.querySelector('input[name="occurrence_start_time"]');
      const endInput = occurrenceEl.querySelector('input[name="occurrence_end_time"]');

      syncedOccurrences.push({
        start_time: startInput?.value || '',
        end_time: endInput?.value || ''
      });
    });

    this.occurrences = syncedOccurrences;
  }

  updateOccurrencesSection() {
    const container = this.querySelector('#occurrences-container');
    if (container) {
      container.innerHTML = this.renderOccurrences();

      // Reattach remove buttons
      container.querySelectorAll('.btn-remove-occurrence').forEach(btn => {
        btn.addEventListener('click', (e) => {
          const index = parseInt(e.target.dataset.index);
          this.occurrences.splice(index, 1);
          this.updateOccurrencesSection();
        });
      });
    }
  }

  updateRecurrencePatternFields() {
    const recurrencePattern = this.querySelector('#recurrence_pattern');
    if (!recurrencePattern) {
      return;
    }

    const pattern = recurrencePattern.value || 'weekly';
    const weeklyFields = this.querySelector('#weeklyPatternFields');
    const monthlyFields = this.querySelector('#monthlyPatternFields');
    const dailyFields = this.querySelector('#dailyPatternFields');

    if (weeklyFields) weeklyFields.style.display = pattern === 'weekly' ? 'block' : 'none';
    if (monthlyFields) monthlyFields.style.display = pattern === 'monthly' ? 'block' : 'none';
    if (dailyFields) dailyFields.style.display = pattern === 'daily' ? 'block' : 'none';
  }

  updateLinksSection() {
    const container = this.querySelector('#external-links-container');
    container.innerHTML = this.renderExternalLinks();

    // Reattach link-specific event listeners
    container.querySelectorAll('input[data-field]').forEach(input => {
      input.addEventListener('input', (e) => {
        const index = parseInt(e.target.dataset.index);
        const field = e.target.dataset.field;
        if (this.externalLinks[index]) {
          this.externalLinks[index][field] = e.target.value;
        }
      });
    });

    container.querySelectorAll('.btn-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const index = parseInt(e.target.dataset.index);
        this.externalLinks.splice(index, 1);
        this.updateLinksSection();
      });
    });
  }

  async handleSubmit() {
    const form = this.querySelector('#eventForm');
    const formData = new FormData(form);

    // datetime-local gives us the value in the user's local time
    // Format: "2025-10-30T20:00" -> convert to "2025-10-30 20:00:00"
    const startTimeLocal = formData.get('start_time');
    const endTimeLocal = formData.get('end_time');

    // Get external links from form inputs
    const externalLinks = [];
    const linkInputs = this.querySelectorAll('.external-link-input');
    linkInputs.forEach(input => {
      const labelInput = input.querySelector('[data-field="label"]');
      const urlInput = input.querySelector('[data-field="url"]');
      if (urlInput && urlInput.value.trim()) {
        externalLinks.push({
          label: labelInput.value.trim() || urlInput.value.trim(),
          url: urlInput.value.trim()
        });
      }
    });

    const eventData = {
      title: formData.get('title'),
      description: formData.get('description') || undefined,
      location: formData.get('location') || undefined,
      is_public: this.presetGroupId && !this.isEditMode
        ? false
        : formData.get('is_public') === 'true',
      show_rsvp_count: (this.presetGroupId && !this.isEditMode) || formData.get('is_public') !== 'true'
        ? true
        : (formData.get('show_rsvp_count') === 'on'),
      external_links: externalLinks,
      timezone: this.selectedTimezone
    };

    const groupIdRaw = formData.get('group_id');
    if (groupIdRaw !== null && String(groupIdRaw).trim() !== '') {
      const parsedG = parseInt(String(groupIdRaw), 10);
      if (!Number.isNaN(parsedG)) {
        eventData.group_id = parsedG;
      }
    } else if (this.isEditMode) {
      eventData.group_id = null;
    }

    // Handle event type-specific data
    if (this.eventType === 'one-time') {
      eventData.start_time = formatLocalDateTimeToMySQL(startTimeLocal, this.selectedTimezone);
      eventData.end_time = formatLocalDateTimeToMySQL(endTimeLocal, this.selectedTimezone);
      if (!eventData.start_time || !eventData.end_time) {
        alert('Please enter valid start and end dates and times.');
        const submitBtn = this.querySelector('#submitBtn');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = this.isEditMode ? 'Update Event' : 'Create Event';
        }
        return;
      }
    } else if (this.eventType === 'recurring') {
      eventData.is_recurring = true;
      eventData.recurrence_pattern = formData.get('recurrence_pattern');
      eventData.recurrence_rule = this.buildRecurrenceRule(formData);

      const recurringStartLocal = formData.get('recurring_start_time');
      const recurringEndLocal = formData.get('recurring_end_time');
      eventData.start_time = formatLocalDateTimeToMySQL(recurringStartLocal, this.selectedTimezone);
      eventData.end_time = formatLocalDateTimeToMySQL(recurringEndLocal, this.selectedTimezone);
      if (!eventData.start_time || !eventData.end_time) {
        alert('Please enter valid start and end dates and times.');
        const submitBtn = this.querySelector('#submitBtn');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = this.isEditMode ? 'Update Event' : 'Create Event';
        }
        return;
      }

      // Series end date is optional - only include if provided
      // Note: series_end_date is just a date (YYYY-MM-DD), so capture the final second of that day
      const seriesEnd = formData.get('series_end_date');
      if (seriesEnd) {
        // Parse as local date at 23:59:59 so we cover the entire day in the selected timezone
        const seriesEndLocal = `${seriesEnd}T23:59:59`;
        eventData.series_end_date = formatLocalDateTimeToMySQL(seriesEndLocal, this.selectedTimezone);
      }

      // RSVP Reminder configuration (only for parent recurring events)
      const rsvpReminderEnabled = formData.get('rsvp_reminder_enabled') === 'on';
      if (rsvpReminderEnabled) {
        const daysBefore = parseInt(formData.get('rsvp_reminder_days') || '0', 10);
        const hoursBefore = parseInt(formData.get('rsvp_reminder_hours') || '0', 10);
        const totalMinutes = (daysBefore * 24 * 60) + (hoursBefore * 60);

        // Validation
        if (totalMinutes <= 0) {
          alert('Please specify when to send the reminder (at least one day or hour must be greater than 0).');
          const submitBtn = this.querySelector('#submitBtn');
          submitBtn.disabled = false;
          submitBtn.textContent = this.isEditMode ? 'Update Event' : 'Create Event';
          return;
        }

        const notificationTypes = [];
        if (formData.get('rsvp_reminder_email') === 'on') {
          notificationTypes.push('email');
        }
        if (formData.get('rsvp_reminder_sms') === 'on') {
          notificationTypes.push('sms');
        }

        if (notificationTypes.length === 0) {
          alert('Please select at least one notification type (Email or SMS).');
          const submitBtn = this.querySelector('#submitBtn');
          submitBtn.disabled = false;
          submitBtn.textContent = this.isEditMode ? 'Update Event' : 'Create Event';
          return;
        }

        eventData.rsvp_reminder_offset_minutes = totalMinutes;
        eventData.rsvp_reminder_notification_types = notificationTypes;

        const reminderMessage = formData.get('rsvp_reminder_message')?.trim();
        if (reminderMessage) {
          eventData.rsvp_reminder_message = reminderMessage;
        }
      } else {
        // Explicitly disable if toggle is off
        eventData.rsvp_reminder_offset_minutes = null;
        eventData.rsvp_reminder_notification_types = null;
        eventData.rsvp_reminder_message = null;
      }
    } else if (this.eventType === 'multi-day') {
      eventData.is_multi_day = true;
      eventData.occurrences = this.buildOccurrences();
      // For multi-day, we need to set a parent start_time from the first occurrence
      const firstOccurrence = eventData.occurrences.find(occ => occ.start_time);
      eventData.start_time = firstOccurrence ? firstOccurrence.start_time : undefined;
    }

    if (this.shouldShowGroupMemberNotify()) {
      eventData.notify_group_members = formData.get('notify_group_members') === 'on';
      if (eventData.notify_group_members) {
        const groupMessage = formData.get('group_notify_message')?.trim();
        if (groupMessage) {
          eventData.group_notify_message = groupMessage;
        }
      }
    } else if (this.isEditMode) {
      eventData.send_update_email = formData.get('send_update_email') === 'on';
      if (eventData.send_update_email) {
        const message = formData.get('update_message')?.trim();
        if (message) {
          eventData.update_message = message;
        }
      }
    }

    try {
      this.clearAlerts();
      const submitBtn = this.querySelector('#submitBtn');
      submitBtn.disabled = true;
      submitBtn.textContent = this.isEditMode ? 'Updating...' : 'Creating...';

      let event;
      if (this.isEditMode) {
        event = await window.API.request(`/events/${this.event.id}`, {
          method: 'PUT',
          body: JSON.stringify(eventData)
        });
      } else {
        event = await window.API.request('/events', {
          method: 'POST',
          body: JSON.stringify(eventData)
        });
      }

      // Show success message
      this.showAlert(this.isEditMode ? 'Event updated successfully!' : 'Event created successfully!', 'success');

      // Navigate after save
      setTimeout(() => {
        if (this.presetGroupId && !this.isEditMode) {
          window.router.navigate(`/groups/${this.presetGroupId}`);
        } else {
          window.router.navigate(`/events/${event.id}`);
        }
      }, 1000);

    } catch (error) {
      window.logger.error(`Failed to ${this.isEditMode ? 'update' : 'create'} event:`, error);
      alert(`Failed to ${this.isEditMode ? 'update' : 'create'} event. Please try again.`);

      const submitBtn = this.querySelector('#submitBtn');
      submitBtn.disabled = false;
      submitBtn.textContent = this.isEditMode ? 'Update Event' : 'Create Event';
    }
  }

  setupPandaTips() {
    const tips = {
      details: [
        'Great titles make people excited! What\'s the vibe? 🎨',
        'A catchy title is the first step to an awesome event! ✨',
        'Tell me about your event - I\'m already excited! 🎊'
      ],
      datetime: [
        'Pick a time that works for everyone! ⏰',
        'Don\'t worry, times will show correctly for all your friends! 🌍',
        'Timing is everything - let\'s find the perfect moment! 📅'
      ],
      links: [
        'Got tickets? A venue website? Add them here! 🎟️',
        'Links help your friends get all the details they need! 🔗',
        'Optional, but super helpful for your guests! 💡'
      ]
    };

    let currentSection = 'details';
    const pandaTipText = this.querySelector('#pandaTipText');

    // Function to get a random tip for the current section
    const updateTip = (section) => {
      if (section !== currentSection) {
        currentSection = section;
        const sectionTips = tips[section] || tips.details;
        const randomTip = sectionTips[Math.floor(Math.random() * sectionTips.length)];
        pandaTipText.textContent = randomTip;

        // Add a little animation to the panda when tip changes
        const pandaHelper = this.querySelector('.party-panda-helper');
        pandaHelper.classList.add('tip-change');
        setTimeout(() => pandaHelper.classList.remove('tip-change'), 500);
      }
    };

    // Observe which form section is in view
    const formSections = this.querySelectorAll('.form-section');
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionTitle = entry.target.querySelector('h2')?.textContent.toLowerCase();
          if (sectionTitle?.includes('event details')) {
            updateTip('details');
          } else if (sectionTitle?.includes('date')) {
            updateTip('datetime');
          } else if (sectionTitle?.includes('link')) {
            updateTip('links');
          }
        }
      });
    }, observerOptions);

    formSections.forEach(section => observer.observe(section));

    // Also update on input focus
    const titleInput = this.querySelector('#title');
    const dateInputs = this.querySelectorAll('input[type="datetime-local"]');

    titleInput?.addEventListener('focus', () => updateTip('details'));
    dateInputs.forEach(input => {
      input.addEventListener('focus', () => updateTip('datetime'));
    });
  }


  buildRecurrenceRule(formData) {
    const pattern = formData.get('recurrence_pattern');
    const rule = { pattern, timezone: this.selectedTimezone };

    if (pattern === 'weekly') {
      const days = formData.getAll('days_of_week');
      rule.days_of_week = days.map(d => parseInt(d));
      rule.interval = parseInt(formData.get('weekly_interval') || '1');
    } else if (pattern === 'monthly') {
      const monthlyType = formData.get('monthly_pattern_type');
      if (monthlyType === 'exact') {
        rule.day_of_month = parseInt(formData.get('monthly_day_number') || '1');
      } else if (monthlyType === 'relative') {
        rule.day_of_month = formData.get('monthly_relative');
      }
      rule.interval = 1;
    } else if (pattern === 'daily') {
      rule.interval = parseInt(formData.get('daily_interval') || '1');
    }

    return rule;
  }

  buildOccurrences() {
    const occurrences = [];
    const occurrenceInputs = this.querySelectorAll('.occurrence-input');

    occurrenceInputs.forEach(occurrenceEl => {
      const startInput = occurrenceEl.querySelector('input[name="occurrence_start_time"]');
      const endInput = occurrenceEl.querySelector('input[name="occurrence_end_time"]');

      if (startInput && startInput.value) {
        occurrences.push({
          start_time: formatLocalDateTimeToMySQL(startInput.value, this.selectedTimezone),
          end_time: endInput?.value ? formatLocalDateTimeToMySQL(endInput.value, this.selectedTimezone) : undefined
        });
      }
    });

    return occurrences;
  }

  renderRecurringFields() {
    const isEditingRecurring = this.isEditMode && this.eventType === 'recurring' && !!this.event;
    const recurrenceRule = isEditingRecurring ? (this.event?.recurrence_rule || {}) : {};
    const recurrencePattern = isEditingRecurring
      ? (this.event?.recurrence_pattern || recurrenceRule.pattern || this.recurrencePattern || 'weekly')
      : (this.recurrencePattern || 'weekly');

    const selectedDays = new Set(
      Array.isArray(recurrenceRule.days_of_week)
        ? recurrenceRule.days_of_week.map((day) => String(day))
        : []
    );
    const weeklyInterval = recurrencePattern === 'weekly'
      ? parseInt(recurrenceRule.interval || 1, 10)
      : 1;
    const dailyInterval = recurrencePattern === 'daily'
      ? parseInt(recurrenceRule.interval || 1, 10)
      : 1;

    let monthlyPatternType = 'exact';
    let monthlyDayNumber = 1;
    if (typeof recurrenceRule.day_of_month === 'number') {
      monthlyDayNumber = recurrenceRule.day_of_month;
    } else if (
      typeof recurrenceRule.day_of_month === 'string' &&
      !isNaN(Number(recurrenceRule.day_of_month))
    ) {
      monthlyDayNumber = parseInt(recurrenceRule.day_of_month, 10);
    }
    let monthlyRelativeValue = recurrenceRule.monthly_relative || 'first-monday';

    if (recurrencePattern === 'monthly') {
      if (typeof recurrenceRule.monthly_relative === 'string') {
        monthlyPatternType = 'relative';
        monthlyRelativeValue = recurrenceRule.monthly_relative;
      } else if (
        typeof recurrenceRule.day_of_month === 'string' &&
        isNaN(Number(recurrenceRule.day_of_month))
      ) {
        monthlyPatternType = 'relative';
        monthlyRelativeValue = recurrenceRule.day_of_month;
      } else {
        monthlyPatternType = 'exact';
      }
      if (monthlyPatternType === 'exact') {
        monthlyDayNumber = Math.max(1, Math.min(31, monthlyDayNumber || 1));
      }
    }

    const recurringStartValue = isEditingRecurring && this.event?.start_time
      ? formatDateTimeForInput(
        parseEventDate(this.event.start_time, this.selectedTimezone),
        this.selectedTimezone
      )
      : '';
    const recurringEndValue = isEditingRecurring && this.event?.end_time
      ? formatDateTimeForInput(
        parseEventDate(this.event.end_time, this.selectedTimezone),
        this.selectedTimezone
      )
      : '';

    let seriesEndDateValue = '';
    if (isEditingRecurring && this.event?.series_end_date) {
      const formatted = formatDateTimeForInput(
        parseEventDate(this.event.series_end_date, this.selectedTimezone),
        this.selectedTimezone
      );
      seriesEndDateValue = formatted ? formatted.slice(0, 10) : '';
    }

    const recurrenceOptions = [
      { value: 'weekly', label: 'Weekly' },
      { value: 'monthly', label: 'Monthly' },
      { value: 'daily', label: 'Daily' },
      { value: 'custom', label: 'Custom' }
    ];

    const weeklyIntervals = [
      { value: 1, label: 'Every week' },
      { value: 2, label: 'Every 2 weeks' },
      { value: 3, label: 'Every 3 weeks' },
      { value: 4, label: 'Every 4 weeks' }
    ];

    const monthlyRelativeOptions = [
      'first-monday', 'first-tuesday', 'first-wednesday', 'first-thursday', 'first-friday', 'first-saturday', 'first-sunday',
      'second-monday', 'second-tuesday', 'second-wednesday', 'second-thursday', 'second-friday', 'second-saturday', 'second-sunday',
      'third-monday', 'third-tuesday', 'third-wednesday', 'third-thursday', 'third-friday', 'third-saturday', 'third-sunday',
      'fourth-monday', 'fourth-tuesday', 'fourth-wednesday', 'fourth-thursday', 'fourth-friday', 'fourth-saturday', 'fourth-sunday',
      'last-monday', 'last-tuesday', 'last-wednesday', 'last-thursday', 'last-friday', 'last-saturday', 'last-sunday'
    ];
    const monthlyPositionMap = {
      first: 'First',
      second: 'Second',
      third: 'Third',
      fourth: 'Fourth',
      last: 'Last'
    };
    const monthlyDayMap = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };

    return `
      <div class="form-group">
        <label for="recurrence_pattern">Repeat</label>
        <select id="recurrence_pattern" name="recurrence_pattern">
          ${recurrenceOptions.map(option => `
            <option value="${option.value}" ${recurrencePattern === option.value ? 'selected' : ''}>
              ${option.label}
            </option>
          `).join('')}
        </select>
      </div>

      <div id="weeklyPatternFields" style="display: ${recurrencePattern === 'weekly' ? 'block' : 'none'};">
        <div class="form-group">
          <label>Days of Week</label>
          <div class="checkbox-group">
            <label class="checkbox-label">
              <input type="checkbox" name="days_of_week" value="0" ${selectedDays.has('0') ? 'checked' : ''}>
              <span>Sunday</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" name="days_of_week" value="1" ${selectedDays.has('1') ? 'checked' : ''}>
              <span>Monday</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" name="days_of_week" value="2" ${selectedDays.has('2') ? 'checked' : ''}>
              <span>Tuesday</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" name="days_of_week" value="3" ${selectedDays.has('3') ? 'checked' : ''}>
              <span>Wednesday</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" name="days_of_week" value="4" ${selectedDays.has('4') ? 'checked' : ''}>
              <span>Thursday</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" name="days_of_week" value="5" ${selectedDays.has('5') ? 'checked' : ''}>
              <span>Friday</span>
            </label>
            <label class="checkbox-label">
              <input type="checkbox" name="days_of_week" value="6" ${selectedDays.has('6') ? 'checked' : ''}>
              <span>Saturday</span>
            </label>
          </div>
        </div>

        <div class="form-group">
          <label for="weekly_interval">Every</label>
          <select id="weekly_interval" name="weekly_interval">
            ${weeklyIntervals.map(option => `
              <option value="${option.value}" ${weeklyInterval === option.value ? 'selected' : ''}>
                ${option.label}
              </option>
            `).join('')}
          </select>
        </div>
      </div>

      <div id="monthlyPatternFields" style="display: ${recurrencePattern === 'monthly' ? 'block' : 'none'};">
        <div class="form-group">
          <label>Monthly Pattern</label>
          <div class="radio-group">
            <label class="radio-label">
              <input type="radio" name="monthly_pattern_type" value="exact" ${monthlyPatternType === 'exact' ? 'checked' : ''}>
              <div class="radio-card">
                <strong>📅 Exact Day</strong>
                <small>Always on specific day</small>
              </div>
            </label>
            <label class="radio-label">
              <input type="radio" name="monthly_pattern_type" value="relative" ${monthlyPatternType === 'relative' ? 'checked' : ''}>
              <div class="radio-card">
                <strong>🗓️ Nth Weekday</strong>
                <small>First/Second/etc.</small>
              </div>
            </label>
          </div>
        </div>

        <div id="monthlyExactFields" style="display: ${monthlyPatternType === 'exact' ? 'block' : 'none'};">
          <div class="form-group">
            <label for="monthly_day_number">Day of Month</label>
            <input type="number" id="monthly_day_number" name="monthly_day_number" min="1" max="31" value="${monthlyDayNumber}">
          </div>
        </div>

        <div id="monthlyRelativeFields" style="display: ${monthlyPatternType === 'relative' ? 'block' : 'none'};">
          <div class="form-group">
            <label>Which Weekday</label>
            <select name="monthly_relative">
              ${monthlyRelativeOptions.map(option => {
    const [position, day] = option.split('-');
    const label = `${monthlyPositionMap[position] || position} ${monthlyDayMap[day] || day}`;
    return `
                  <option value="${option}" ${monthlyRelativeValue === option ? 'selected' : ''}>
                    ${label}
                  </option>
                `;
  }).join('')}
            </select>
          </div>
        </div>
      </div>

      <div id="dailyPatternFields" style="display: ${recurrencePattern === 'daily' ? 'block' : 'none'};">
        <div class="form-group">
          <label for="daily_interval">Every</label>
          <select id="daily_interval" name="daily_interval">
            <option value="1" ${dailyInterval === 1 ? 'selected' : ''}>Every day</option>
            <option value="2" ${dailyInterval === 2 ? 'selected' : ''}>Every 2 days</option>
            <option value="3" ${dailyInterval === 3 ? 'selected' : ''}>Every 3 days</option>
            <option value="4" ${dailyInterval === 4 ? 'selected' : ''}>Every 4 days</option>
          </select>
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="recurring_start_time">Start Date & Time *</label>
          <input 
            type="datetime-local" 
            id="recurring_start_time" 
            name="recurring_start_time"
            required
            value="${recurringStartValue}"
          >
        </div>

        <div class="form-group">
          <label for="recurring_end_time">End Date & Time *</label>
          <input 
            type="datetime-local" 
            id="recurring_end_time" 
            name="recurring_end_time"
            required
            value="${recurringEndValue}"
          >
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label for="series_end_date">Series End Date <small>(optional)</small></label>
          <input 
            type="date" 
            id="series_end_date" 
            name="series_end_date"
            value="${seriesEndDateValue}"
          >
          <small class="help-text">
            Leave empty for events that continue indefinitely
          </small>
        </div>
      </div>

      <div class="form-group">
        <small class="help-text">
          📍 Times are saved using the selected timezone.
        </small>
      </div>
    `;
  }

  renderMultiDayFields() {
    return `
      <div class="form-group">
        <label>Event Occurrences</label>
        <p class="help-text">
          Add each date and time for your multi-day event (e.g., theater showtimes)
        </p>

        <div id="occurrences-container">
          ${this.renderOccurrences()}
        </div>

        <button type="button" class="btn btn-secondary" id="addOccurrenceBtn">
          + Add Another Date
        </button>
      </div>

      <div class="form-group">
        <small class="help-text">
          📍 Times are saved using the selected timezone.
        </small>
      </div>
    `;
  }

  renderOccurrences() {
    // If empty, show one empty occurrence to start with
    if (this.occurrences.length === 0) {
      return `
        <div class="occurrence-input" data-index="0">
          <div class="form-row">
            <div class="form-group">
              <label>Start Date & Time *</label>
              <input type="datetime-local" name="occurrence_start_time" required>
            </div>
            <div class="form-group">
              <label>End Date & Time *</label>
              <input type="datetime-local" name="occurrence_end_time" required>
            </div>
            <div class="form-group">
              <button type="button" class="btn-remove-occurrence" data-index="0" style="display: none;">×</button>
            </div>
          </div>
        </div>
      `;
    }

    return this.occurrences.map((occ, index) => `
      <div class="occurrence-input" data-index="${index}">
        <div class="form-row">
          <div class="form-group">
            <label>Start Date & Time *</label>
            <input type="datetime-local" name="occurrence_start_time" value="${occ.start_time ? formatDateTimeForInput(parseEventDate(occ.start_time, this.selectedTimezone), this.selectedTimezone) : ''}" required>
          </div>
          <div class="form-group">
            <label>End Date & Time *</label>
            <input type="datetime-local" name="occurrence_end_time" value="${occ.end_time ? formatDateTimeForInput(parseEventDate(occ.end_time, this.selectedTimezone), this.selectedTimezone) : ''}" required>
          </div>
          <div class="form-group">
            <button type="button" class="btn-remove-occurrence" data-index="${index}">×</button>
          </div>
        </div>
      </div>
    `).join('');
  }

  shouldShowGroupMemberNotify() {
    if (this.isEditMode) {
      return Boolean(this.event?.group_id);
    }
    return Boolean(this.presetGroupId);
  }

  renderNotifyGroupMembersSection() {
    const isUpdate = this.isEditMode;
    return `
      <div class="form-section notify-group-members-section">
        <h2>Notify group members <small>(optional)</small></h2>
        <p class="help-text">
          Group members are not emailed automatically when you ${isUpdate ? 'save changes' : 'create this event'}.
          Check the box below to send an email.
        </p>

        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              id="notify_group_members"
              name="notify_group_members"
            >
            <span>Email group members</span>
          </label>
        </div>

        <div id="notifyGroupMembersFields" class="notify-group-members-fields" style="display: none;">
          <div class="form-group">
            <label for="group_notify_message">Message for group members <small>(optional)</small></label>
            <textarea
              id="group_notify_message"
              name="group_notify_message"
              rows="3"
              maxlength="1000"
              placeholder="e.g. Bring a dish to share — looking forward to seeing everyone!"
            ></textarea>
            <small class="help-text">Included in the email so members know what to expect.</small>
          </div>
        </div>
      </div>
    `;
  }

  renderSendUpdateEmailSection() {
    return `
      <div class="form-section send-update-email-section">
        <h2>Notify Invitees <small>(optional)</small></h2>
        <p class="help-text">
          Invitees are not emailed automatically when you save changes.
          Check the box below to send an &quot;event has been updated&quot; email.
        </p>

        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              id="send_update_email"
              name="send_update_email"
            >
            <span>Send update email to invitees</span>
          </label>
        </div>

        <div id="sendUpdateEmailFields" class="send-update-email-fields" style="display: none;">
          <div class="form-group">
            <label for="update_message">Message for invitees <small>(optional)</small></label>
            <textarea
              id="update_message"
              name="update_message"
              rows="3"
              maxlength="1000"
              placeholder="e.g. We moved to the back room — same time!"
            ></textarea>
            <small class="help-text">Included in the update email so invitees know what changed.</small>
          </div>
        </div>
      </div>
    `;
  }

  renderRsvpReminderSection() {
    const rsvpReminderEnabled = this.event?.rsvp_reminder_offset_minutes !== null &&
      this.event?.rsvp_reminder_offset_minutes !== undefined;
    const offsetMinutes = this.event?.rsvp_reminder_offset_minutes || 1440; // Default to 1 day
    const daysBefore = Math.floor(offsetMinutes / (24 * 60));
    const hoursBefore = Math.floor((offsetMinutes % (24 * 60)) / 60);

    // Parse notification types - could be array, JSON string, or null
    let notificationTypes = this.event?.rsvp_reminder_notification_types || [];
    if (typeof notificationTypes === 'string') {
      try {
        notificationTypes = JSON.parse(notificationTypes);
      } catch (_e) {
        notificationTypes = [];
      }
    }
    if (!Array.isArray(notificationTypes)) {
      notificationTypes = [];
    }

    const hasEmail = notificationTypes.includes('email');
    const hasSms = notificationTypes.includes('sms');

    const reminderMessage = this.event?.rsvp_reminder_message || '';

    return `
      <div class="form-section">
        <h2>RSVP Reminders <small>(optional)</small></h2>
        <p class="help-text">
          Automatically remind invited guests to RSVP for each instance of this recurring event.
          Reminders are only sent to users who haven't already RSVPed.
        </p>

        <div class="form-group">
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              id="rsvp_reminder_enabled" 
              name="rsvp_reminder_enabled"
              ${rsvpReminderEnabled ? 'checked' : ''}
            >
            <span>Send automatic RSVP reminders for each instance</span>
          </label>
        </div>

        <div id="rsvpReminderFields" style="display: ${rsvpReminderEnabled ? 'block' : 'none'};">
          <div class="form-group">
            <label>When to send reminder</label>
            <div class="form-row">
              <div class="form-group">
                <label for="rsvp_reminder_days">Days before</label>
                <input 
                  type="number" 
                  id="rsvp_reminder_days" 
                  name="rsvp_reminder_days" 
                  min="0" 
                  max="365"
                  value="${daysBefore}"
                >
              </div>
              <div class="form-group">
                <label for="rsvp_reminder_hours">Hours before</label>
                <input 
                  type="number" 
                  id="rsvp_reminder_hours" 
                  name="rsvp_reminder_hours" 
                  min="0" 
                  max="23"
                  value="${hoursBefore}"
                >
              </div>
            </div>
            <small class="help-text">
              At least one value must be greater than 0
            </small>
          </div>

          <div class="form-group">
            <label>Notification types</label>
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  id="rsvp_reminder_email" 
                  name="rsvp_reminder_email"
                  ${hasEmail ? 'checked' : ''}
                >
                <span>📧 Email</span>
              </label>
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  id="rsvp_reminder_sms" 
                  name="rsvp_reminder_sms"
                  ${hasSms ? 'checked' : ''}
                >
                <span>📱 SMS</span>
              </label>
            </div>
            <small class="help-text">
              At least one notification type must be selected
            </small>
          </div>

          <div class="form-group">
            <label for="rsvp_reminder_message">Custom message <small>(optional)</small></label>
            <textarea 
              id="rsvp_reminder_message" 
              name="rsvp_reminder_message" 
              rows="3"
              placeholder="e.g., Don't forget to RSVP for this week's game night!"
              maxlength="500"
            >${escapeHtml(reminderMessage)}</textarea>
            <small class="help-text">
              Optional custom message to include in the reminder. If left empty, a default message will be used.
            </small>
          </div>
        </div>
      </div>
    `;
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('.alert-container');
    const alert = document.createElement('div');
    alert.className = `alert alert-${type}`;
    alert.textContent = message;

    container.appendChild(alert);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove();
      }
    }, 5000);
  }

  clearAlerts() {
    const container = this.querySelector('.alert-container');
    container.innerHTML = '';
  }
}

customElements.define('event-edit', EventEdit);
